import type { Theme } from '@onefootprint/design-tokens';
import themes from '@onefootprint/design-tokens';
import type { FootprintAppearance, FootprintAppearanceVariables } from '@onefootprint/footprint-js';

import createRules from './utils/create-rules';
import createStyle from './utils/create-style';
import createTheme, { createCssVariables } from './utils/create-theme';
import getAppearanceFromObConfig from './utils/get-appearance-from-ob-config';
import getAppearanceFromStyleParams from './utils/get-appearance-from-style-params';
import getAppearanceFromUrl from './utils/get-appearance-from-url';

const IS_BROWSER = typeof window !== 'undefined';

const extractFontFamiliesFromUrl = (fontSrc: string): string | null => {
  try {
    const url = new URL(fontSrc);
    const families = url.searchParams.getAll('family');
    if (families.length === 0) return null;
    return families.map(f => `'${f.split(':')[0].replace(/\+/g, ' ')}'`).join(', ');
  } catch {
    return null;
  }
};

type Strategy = 'queryParameters' | 'obConfig' | 'styleParams' | 'onboardingSessionToken';

type AppearanceOptions = {
  variant?: string;
  defaultTheme?: Theme;
  params?: Record<string, string>;
  strategy: Strategy[];
  authToken?: string;
  kybBoAuthToken?: string;
  obConfig?: string;
};

type AppearanceResponse = {
  appearance: FootprintAppearance | null;
  variant: string | null;
  fontSrc: string | null;
  rules: string | null;
  variables: FootprintAppearanceVariables | null;
  theme: Theme;
};

const prefersDarkMode = (themePreference: string | undefined): boolean =>
  themePreference === 'dark' ||
  (themePreference === 'system' &&
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches);

const buildAppearanceResponse = (
  appearance: FootprintAppearance,
  defaultTheme: Theme,
  variant: string,
): AppearanceResponse => {
  const baseTheme = prefersDarkMode(appearance.theme) ? themes.dark : defaultTheme;
  return {
    appearance,
    fontSrc: appearance.fontSrc || null,
    rules: createRules(appearance.rules) || null,
    theme: createTheme(baseTheme, appearance.variables),
    variables: appearance.variables || null,
    variant: appearance.variant || variant,
  };
};

const getCustomAppearance = async ({
  variant = 'modal',
  defaultTheme = themes.light,
  params,
  strategy,
  authToken,
  obConfig,
  kybBoAuthToken,
}: AppearanceOptions): Promise<AppearanceResponse> => {
  if (strategy.includes('queryParameters')) {
    const variables = params?.variables || params?.tokens;
    if (variables || params?.rules || params?.theme || params?.font_src) {
      const appearance = await getAppearanceFromUrl({ ...params, variables });
      const appearanceFromObConfig = await getAppearanceFromObConfig({
        obConfig,
        authToken,
        kybBoAuthToken,
      });
      if (appearance) {
        return buildAppearanceResponse(
          {
            ...appearance,
            theme: appearance.theme ?? appearanceFromObConfig?.theme ?? null,
          } as FootprintAppearance,
          defaultTheme,
          variant,
        );
      }
    }
  }
  if (strategy.includes('styleParams')) {
    if (authToken) {
      const appearance = await getAppearanceFromStyleParams(authToken);
      if (appearance) {
        return buildAppearanceResponse(appearance, defaultTheme, variant);
      }
    }
  }
  if (strategy.includes('onboardingSessionToken')) {
    const appearance = await getAppearanceFromObConfig({
      obConfig,
      authToken,
    });
    if (appearance) {
      return buildAppearanceResponse(appearance, defaultTheme, variant);
    }
  }

  if (strategy.includes('obConfig')) {
    const appearance = await getAppearanceFromObConfig({
      obConfig,
      authToken,
      kybBoAuthToken,
    });
    if (appearance) {
      return buildAppearanceResponse(appearance, defaultTheme, variant);
    }
  }
  return {
    appearance: null,
    fontSrc: null,
    rules: null,
    theme: defaultTheme,
    variables: null,
    variant,
  };
};

const appendClientSideStyles =
  (getCustomAppearanceFn: (options: AppearanceOptions) => Promise<AppearanceResponse>) =>
  async (options: AppearanceOptions) => {
    const appearance = await getCustomAppearanceFn(options);
    if (!appearance || !IS_BROWSER) {
      return appearance;
    }

    if (appearance.fontSrc) {
      createStyle('footprint-custom-fonts', `@import url('${appearance.fontSrc}');`);
    }
    if (appearance.rules) {
      createStyle('footprint-custom-rules', appearance.rules);
    }
    const cssVariables = createCssVariables(appearance.theme);
    if (cssVariables) {
      createStyle('footprint-custom-variables', cssVariables);
    }
    if (appearance.fontSrc && !appearance.variables?.fontFamily) {
      const fontFamily = extractFontFamiliesFromUrl(appearance.fontSrc);
      if (fontFamily) {
        createStyle('footprint-font-family', `:root { --font-family-default: ${fontFamily}; }`);
      }
    }
    if (appearance.variant) {
      document.body.setAttribute('data-variant', appearance.variant);
    }

    const themePreference = (appearance.appearance as Record<string, unknown> | null)?.theme;
    const isDark = prefersDarkMode(themePreference as string | undefined);
    document.body.classList.toggle('dark', isDark);

    return appearance;
  };

export type { Strategy };
export default appendClientSideStyles(getCustomAppearance);
